import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { cx } from "../../cx";

export interface RowMenuProps {
  /** Accessible name of the trigger (e.g. «Actions»). */
  readonly label: string;
  /** Menu content: buttons/links; they keep their own handlers. */
  readonly children: ReactNode;
  readonly className?: string;
  readonly testId?: string;
}

const MENU_GAP = 4;
const MENU_MAX_HEIGHT = 280;

/**
 * Per-row actions menu for DataTable (`rowActions`). Native disclosure, so keyboard and
 * screen-reader support come for free. The panel is `position: fixed` and placed from the
 * trigger's rect on open, so the table's `overflow` wrapper can never clip it (Q4-CHN-01); it
 * flips above the trigger when there is no room below, closes on outside click / scroll /
 * Escape / choosing an item, and aligns to the inline end in both directions.
 */
export function RowMenu({ label, children, className, testId }: RowMenuProps) {
  const ref = useRef<HTMLDetailsElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const openedAt = useRef<{ top: number; left: number } | null>(null);
  const [pos, setPos] = useState<CSSProperties>({});

  const place = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    openedAt.current = { top: rect.top, left: rect.left };
    const rtl = getComputedStyle(el).direction === "rtl";
    const below = window.innerHeight - rect.bottom;
    const flip = below < MENU_MAX_HEIGHT && rect.top > below;
    setPos({
      insetInlineEnd: rtl ? rect.left : window.innerWidth - rect.right,
      ...(flip
        ? { bottom: window.innerHeight - rect.top + MENU_GAP }
        : { top: rect.bottom + MENU_GAP }),
    });
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const close = () => {
      el.open = false;
    };
    const onOutside = (e: Event) => {
      if (el.open && !el.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (!el.open) return;
      if (e.key === "Escape") {
        close();
        el.querySelector("summary")?.focus();
        return;
      }
      // Roving focus across the menu items (WAI-ARIA menu pattern).
      const items = Array.from(
        panelRef.current?.querySelectorAll<HTMLElement>("button:not([disabled]), a[href]") ?? [],
      );
      if (items.length === 0) return;
      const at = items.indexOf(document.activeElement as HTMLElement);
      let next = -1;
      if (e.key === "ArrowDown") next = at < 0 ? 0 : (at + 1) % items.length;
      else if (e.key === "ArrowUp") next = at <= 0 ? items.length - 1 : at - 1;
      else if (e.key === "Home") next = 0;
      else if (e.key === "End") next = items.length - 1;
      if (next < 0) return;
      e.preventDefault();
      items[next]?.focus();
    };
    document.addEventListener("pointerdown", onOutside);
    document.addEventListener("keydown", onKey);
    // Close on a scroll that really moves the trigger. Browsers also fire a spurious scroll right
    // after opening (focus / scroll anchoring), which used to shut the menu the moment it opened.
    const onScroll = (e: Event) => {
      if (!el.open) return;
      if (panelRef.current?.contains(e.target as Node)) return;
      const at = openedAt.current;
      const rect = el.getBoundingClientRect();
      // not placed yet (the toggle event is async): that scroll belongs to the opening itself
      if (!at || (Math.abs(rect.top - at.top) < 1 && Math.abs(rect.left - at.left) < 1)) return;
      close();
    };
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("pointerdown", onOutside);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", close);
    };
  }, []);

  // The items are caller-supplied buttons/links: expose them to assistive tech as menu items.
  useEffect(() => {
    panelRef.current
      ?.querySelectorAll("button, a[href]")
      .forEach((item) => item.setAttribute("role", "menuitem"));
  });

  return (
    <details
      ref={ref}
      className={cx("inline-block text-start", className)}
      data-testid={testId}
      onToggle={(e) => {
        if (e.currentTarget.open) {
          place();
          requestAnimationFrame(() =>
            panelRef.current
              ?.querySelector<HTMLElement>("button:not([disabled]), a[href]")
              ?.focus(),
          );
        } else openedAt.current = null;
      }}
    >
      <summary
        aria-label={label}
        className="inline-flex h-8 w-8 cursor-pointer list-none items-center justify-center rounded-md border border-line text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
      >
        <span aria-hidden="true">⋯</span>
      </summary>
      <div
        ref={panelRef}
        role="menu"
        aria-label={label}
        data-testid={testId ? `${testId}-panel` : "row-menu-panel"}
        style={pos}
        onClick={() => {
          if (ref.current) ref.current.open = false;
        }}
        className="fixed z-50 flex min-w-40 flex-col items-stretch gap-1 rounded-md border border-line bg-surface p-2 shadow-md"
      >
        {children}
      </div>
    </details>
  );
}
